import { interpolate, Easing } from 'remotion';

// Constants
import { CLAMP_BOTH } from '@/constants/animations';
import { CURTAIN_ANIMATION, UNFOLD_EASING } from './constants';

const unfoldEasing = Easing.bezier(...UNFOLD_EASING);

export const calculateGeometry = (
  columns: number,
): {
  validColumns: number;
  columnWidthPercent: number;
  actualColumnWidth: number;
} => {
  // Min 1 column
  const validColumns = Math.max(1, Math.floor(columns));
  const columnWidthPercent = 100 / validColumns;

  return {
    validColumns,
    columnWidthPercent,
    // Prevent sub-pixel gaps
    actualColumnWidth: columnWidthPercent + CURTAIN_ANIMATION.OVERLAP_PERCENT,
  };
};

// Calculate total animation duration
export const calculateAnimationEndFrame = (
  delay: number,
  validColumns: number,
  staggerDelay: number,
  columnDuration: number,
): number => {
  return delay + (validColumns - 1) * staggerDelay + columnDuration;
};

export const getColumnAnimationState = (
  frame: number,
  columnDelay: number,
  columnDuration: number,
): {
  scaleX: number;
  opacity: number;
  isVisible: boolean;
} => {
  // Unfold animation
  const scaleX = interpolate(
    frame,
    [columnDelay, columnDelay + columnDuration],
    [0, 1],
    { ...CLAMP_BOTH, easing: unfoldEasing },
  );

  // Fade in early
  const opacity = interpolate(
    frame,
    [
      columnDelay,
      columnDelay + columnDuration * CURTAIN_ANIMATION.OPACITY_FADE_RATIO,
    ],
    [0, 1],
    CLAMP_BOTH,
  );

  return { scaleX, opacity, isVisible: scaleX > 0 || opacity > 0 };
};

// Shadow opacity based on fold state
export const calculateShadowOpacity = (
  scaleX: number,
  showShadow: boolean,
  intensity: number,
): number => {
  if (!showShadow || scaleX <= 0 || scaleX >= 1) return 0;

  // Peak shadow at half-open
  return Math.sin(scaleX * Math.PI) * intensity;
};

export const calculateContentPosition = (
  columnIndex: number,
  columnWidthPercent: number,
  actualColumnWidth: number,
): {
  widthPercent: number;
  leftPercent: number;
} => {
  if (actualColumnWidth === 0) return { widthPercent: 100, leftPercent: 0 };

  // Relative width calculation
  const widthPercent = (100 / actualColumnWidth) * 100;

  // Shift left to compensate offset
  const leftPercent =
    (-(columnIndex * columnWidthPercent) / actualColumnWidth) * 100;

  return { widthPercent, leftPercent };
};
